import { isAnnotationAction } from '@ofmos/shared/record-write';
import type { FieldMetadata, RecordEvent } from '../../data/client';
import { useRecordEvents } from '../../data/hooks';
import { formatDate } from '../../fields/format';
import { Button, EmptyState, Skeleton } from '../../ui';
import { StreamRow } from './StreamRow';

/**
 * The event log of one record, newest first and grouped by day (INV-4).
 *
 * Days are cut in UTC, the same frame the timestamps are rendered in: a local-time
 * grouping over UTC labels puts two entries under a heading neither of them
 * belongs to.
 */
interface DayGroup {
  readonly day: string;
  readonly events: readonly RecordEvent[];
}

/** An annotation event is one whose payload names an annotation; the action agrees. */
function isAnnotationEvent(event: RecordEvent): boolean {
  return isAnnotationAction(event.type);
}

function groupByDay(events: readonly RecordEvent[]): readonly DayGroup[] {
  const newestFirst = [...events].sort((a, b) => b.at.localeCompare(a.at));
  const groups: { day: string; events: RecordEvent[] }[] = [];
  for (const event of newestFirst) {
    const day = event.at.slice(0, 10);
    const current = groups.at(-1);
    if (current !== undefined && current.day === day) {
      current.events.push(event);
    } else {
      groups.push({ day, events: [event] });
    }
  }
  return groups;
}

export interface RecordTimelineProps {
  readonly objectId: string;
  readonly recordId: string;
  readonly fields: readonly FieldMetadata[];
  /**
   * "Changes" means FIELD changes (12.1): the timeline as it was before annotations
   * existed. Eleven new actions make a feed longer, and the chips are the READING answer
   * to that - a chip that shows everything is not an answer, it is a label.
   */
  readonly hideAnnotations?: boolean | undefined;
}

export function RecordTimeline({
  objectId,
  recordId,
  fields,
  hideAnnotations,
}: RecordTimelineProps) {
  const events = useRecordEvents(objectId, recordId);

  if (events.isPending) {
    return (
      <div className="space-y-4 py-3" aria-busy="true">
        <Skeleton className="h-3 w-24" label="Loading the activity" />
        <Skeleton className="h-3 w-48" />
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-3 w-52" />
      </div>
    );
  }

  if (events.isError) {
    return (
      <EmptyState
        icon="warning"
        title="The activity could not be loaded."
        description={events.error.message}
      />
    );
  }

  const loaded = (events.data?.pages ?? []).flatMap((page) => page.events);
  const visible =
    hideAnnotations === true ? loaded.filter((event) => !isAnnotationEvent(event)) : loaded;
  const groups = groupByDay(visible);

  return (
    <div className="record-timeline">
      {groups.length === 0 ? (
        /*
          ONE LINE INSIDE THE STREAM, NOT A BLOCK (section 4, Empty stream). An empty
          state with an icon and two paragraphs is a card in all but name, and this
          column is the content itself.
        */
        <p className="record-meta-line py-4">
          {hideAnnotations === true
            ? 'No changes. Every write to this record lands here.'
            : 'No activity. Add a note to start.'}
        </p>
      ) : (
        <>
          {groups.map((group) => (
            <section key={group.day}>
              <h3 className="record-timeline-day">{formatDate(group.day)}</h3>
              <ol className="record-timeline">
                {group.events.map((event) => (
                  <StreamRow key={event.id} event={event} fields={fields} />
                ))}
              </ol>
            </section>
          ))}
          {/*
            PAGING BACK, not infinite scroll: the newest page loads first and older ones
            are asked for. An audit trail is read from the top, and auto-loading a
            record's whole history because somebody scrolled is how a phone runs out of
            memory on the record that most needs reading.
          */}
          {events.hasNextPage === true && (
            <Button
              variant="ghost"
              block
              loading={events.isFetchingNextPage}
              onClick={() => {
                void events.fetchNextPage();
              }}
            >
              Show more
            </Button>
          )}
        </>
      )}
    </div>
  );
}
